
import { useState } from "react";
import { Prescription } from "@/types";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { useQueueStore } from "@/lib/store";
import { PrescriptionCard } from "./PrescriptionCard";
import { PrescriptionViewerDialog } from "./PrescriptionViewerDialog";

interface PreviousPrescriptionsProps {
  prescriptions: Prescription[];
  patientId: string;
  getDoctorName: (doctorId: string) => string;
  doctors: { id: string; name: string; registrationNumber: string }[];
  showPrintButtons?: boolean;
  onEditDraft?: (prescription: Prescription) => void;
}

export function PreviousPrescriptions({ 
  prescriptions, 
  patientId, 
  getDoctorName,
  doctors,
  showPrintButtons = false,
  onEditDraft
}: PreviousPrescriptionsProps) {
  const [selectedPrescription, setSelectedPrescription] = useState<Prescription | null>(null);
  const { patients } = useQueueStore();
  
  const patient = patients.find(p => p.id === patientId);

  const handleViewPrescription = (prescription: Prescription) => {
    // If it's a draft and we have an edit callback, use that instead
    if (prescription.status === 'draft' && onEditDraft) {
      onEditDraft(prescription);
    } else {
      // For issued prescriptions, show the print preview
      setSelectedPrescription(prescription);
    }
  };
  
  const handlePrint = () => {
    if (!selectedPrescription) return;
    printPrescription();
  };

  const handleDirectPrint = (prescription: Prescription) => {
    setSelectedPrescription(prescription);
    setTimeout(() => {
      printPrescription();
    }, 100);
  };
  
  const printPrescription = () => {
    const printContent = document.getElementById("prescription-print-content");
    if (printContent) {
      const originalContents = document.body.innerHTML;
      document.body.innerHTML = printContent.innerHTML;
      window.print();
      document.body.innerHTML = originalContents;
      window.location.reload();
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Previous Prescriptions</CardTitle>
          <CardDescription>History of prescriptions for this patient</CardDescription>
        </CardHeader>
        <CardContent>
          {prescriptions.length === 0 ? (
            <p className="text-sm text-muted-foreground">No previous prescriptions</p>
          ) : (
            <div className="space-y-4">
              {prescriptions.map((prescription) => (
                <PrescriptionCard
                  key={prescription.id}
                  prescription={prescription}
                  getDoctorName={getDoctorName}
                  onViewPrescription={handleViewPrescription}
                  onPrintPrescription={handleDirectPrint}
                  showPrintButton={showPrintButtons && prescription.status === 'issued'}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      
      {patient && (
        <PrescriptionViewerDialog
          prescription={selectedPrescription}
          isOpen={!!selectedPrescription}
          onOpenChange={(open) => !open && setSelectedPrescription(null)}
          patientName={patient.name}
          patientAge={patient.age}
          patientGender={patient.gender}
          getDoctorName={getDoctorName}
          onPrint={handlePrint}
          doctors={doctors}
        />
      )}
    </>
  );
}
